import {
  SupabaseClient,
  RealtimeChannel,
  REALTIME_SUBSCRIBE_STATES,
} from "@supabase/supabase-js";
import { BaseRepository } from "./base.repository";
import { Chat, ChatInsert, ChatUpdate } from "../entities/chat";
import {
  PaginationOptions,
  PaginatedResponse,
} from "../types/pagination.types";
import { applyPagination } from "../utils/pagination.utils";

export class ChatRepository extends BaseRepository<
  Chat,
  ChatInsert,
  ChatUpdate
> {
  private realtimeChannels: Map<string, RealtimeChannel> = new Map();

  constructor(supabase: SupabaseClient) {
    super(supabase, "chats");
  }

  /**
   * Save a Chat entity (create or update based on dirty state)
   */
  async save(chat: Chat): Promise<Chat> {
    if (!chat.isDirty()) {
      return chat; // No changes to save
    }

    const data = chat.toData();
    const { id, created_at, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedChat = Chat.fromData(saved);
    savedChat.markAsClean();
    return savedChat;
  }

  /**
   * Override findById to return Chat entity
   */
  async findById(id: string): Promise<Chat | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null; // Not found
      throw error;
    }

    return data ? Chat.fromData(data) : null;
  }

  /**
   * Override create to return Chat entity
   */
  async create(data: ChatInsert): Promise<Chat> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return Chat.fromData(created);
  }

  /**
   * Find chats by room
   */
  async findByRoom(roomId: string): Promise<Chat[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data || []).map((d) => Chat.fromData(d));
  }

  /**
   * Find chats by room with pagination
   */
  async findByRoomPaginated(
    roomId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<Chat>> {
    const query = this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .order("created_at", { ascending: false });

    const result = await applyPagination<any, Chat>(query, options, (d) =>
      Chat.fromData(d),
    );
    if ("pagination" in result) {
      return result;
    }
    throw new Error("Expected paginated response");
  }

  /**
   * Find chats by user
   */
  async findByUser(userId: string, limit: number = 50): Promise<Chat[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data || []).map((d) => Chat.fromData(d));
  }

  /**
   * Find chats by role in a room
   */
  async findByRoomAndRole(
    roomId: string,
    role: "user" | "assistant",
  ): Promise<Chat[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .eq("role", role)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data || []).map((d) => Chat.fromData(d));
  }

  /**
   * Get latest chats from a room
   */
  async getLatestChats(roomId: string, limit: number = 20): Promise<Chat[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Reverse to get chronological order
    return (data || []).reverse().map((d) => Chat.fromData(d));
  }

  /**
   * Subscribe to real-time chat updates for a room
   */
  subscribeToRoom(
    roomId: string,
    callbacks: {
      onConnect?: (status: REALTIME_SUBSCRIBE_STATES, err?: Error) => void;
      onInsert?: (chat: Chat) => void;
      onUpdate?: (chat: Chat) => void;
      onDelete?: (id: string) => void;
    },
  ): () => void {
    // Unsubscribe from existing channel if any
    this.unsubscribeFromRoom(roomId);

    const channel = this.supabase
      .channel(`chats:room:${roomId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chats",
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          if (callbacks.onInsert) {
            callbacks.onInsert(Chat.fromData(payload.new));
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "chats",
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          if (callbacks.onUpdate) {
            callbacks.onUpdate(Chat.fromData(payload.new));
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "chats",
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          if (callbacks.onDelete) {
            callbacks.onDelete(payload.old.id);
          }
        },
      )
      .subscribe(callbacks.onConnect);

    this.realtimeChannels.set(roomId, channel);

    // Return unsubscribe function
    return () => this.unsubscribeFromRoom(roomId);
  }

  /**
   * Unsubscribe from real-time updates for a room
   */
  unsubscribeFromRoom(roomId: string): void {
    const channel = this.realtimeChannels.get(roomId);
    if (channel) {
      this.supabase.removeChannel(channel);
      this.realtimeChannels.delete(roomId);
    }
  }

  /**
   * Unsubscribe from all real-time channels
   */
  unsubscribeAll(): void {
    this.realtimeChannels.forEach((channel, roomId) => {
      this.supabase.removeChannel(channel);
    });
    this.realtimeChannels.clear();
  }

  /**
   * Search messages in a room
   */
  async searchInRoom(roomId: string, searchTerm: string): Promise<Chat[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .ilike("message", `%${searchTerm}%`)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data || []).map((d) => Chat.fromData(d));
  }

  /**
   * Search messages in a room with pagination
   */
  async searchInRoomPaginated(
    roomId: string,
    searchTerm: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<Chat>> {
    const query = this.supabase
      .from(this.tableName)
      .select("*")
      .eq("room_id", roomId)
      .ilike("message", `%${searchTerm}%`)
      .order("created_at", { ascending: false });

    const result = await applyPagination<any, Chat>(query, options, (d) =>
      Chat.fromData(d),
    );
    if ("pagination" in result) {
      return result;
    }
    throw new Error("Expected paginated response");
  }

  /**
   * Delete all chats in a room (for cleanup)
   */
  async deleteByRoom(roomId: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq("room_id", roomId);

    if (error) throw error;
  }
}
